import { useAppSelector } from "app/hooks";
import {
  BECollectionFormat,
  SingleCollectionData,
  SingleCollectionListItem,
} from "components/Layout/Collection/types";
import { useAuth } from "hooks/auth";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import { ListingProps } from "types/listing";
import { getEnvVariable } from "utilities/env";

export const useCollections = () => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useCollectionsList = (
    props?: ListingProps,
  ): SWRResponse<
    {
      items: SingleCollectionData[];
      total: { all: number; filtered: number };
    },
    any
  > => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/groupOfValues${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const addCollection = async (values: BECollectionFormat) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/groupOfValues`,
      {
        body: JSON.stringify({ groupOfValues: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editCollection = async (
    collectionId: string,
    values: BECollectionFormat,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/groupOfValues/${collectionId}`,
      {
        body: JSON.stringify({ groupOfValues: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );

    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useSingleCollection = (
    singleCollectionId: string,
    props?: ListingProps,
  ): SWRResponse<SingleCollectionData, any> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/groupOfValues/${singleCollectionId}${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useSingleCollectionValues = (
    singleCollectionId: string,
    props?: ListingProps,
  ): SWRResponse<
    {
      items: SingleCollectionListItem[];
      total: { all: number; filtered: number };
    },
    any
  > => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/groupOfValues/${singleCollectionId}/values${querystring(
              props,
            )}`
          : null,
      fetcher,
    );
  };

  const importCollectionValues = async (
    collectionId: string,
    file?: string | Blob,
  ) => {
    const formData = new FormData();
    file && formData.append("import[file]", file);
    collectionId &&
      formData.append("import[additionalData][groupOfValuesId]", collectionId);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/import/groupValue`,
      { body: formData, method: "post" },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const addCollectionValue = async (values: {
    value: string;
    description: string;
    groupOfValuesId: string;
  }) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/groupOfValues/value`,
      {
        body: JSON.stringify({ value: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editCollectionValue = async (
    valueId: string,
    values: { value: string; description?: string },
  ) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/groupOfValues/value/${valueId}`,
      {
        body: JSON.stringify({ value: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );

    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const deleteCollectionValue = async (valueId: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/groupOfValues/value/${valueId}`,
      { method: "delete" },
    );

    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  return {
    useCollectionsList,
    addCollection,
    editCollection,
    useSingleCollection,
    useSingleCollectionValues,
    importCollectionValues,
    addCollectionValue,
    editCollectionValue,
    deleteCollectionValue,
  };
};
